import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Spinner } from './ui.js';

interface PageSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** The screen is fetching the results of the term: a spinner stands in for the magnifier. */
  busy?: boolean;
  className?: string;
}

/** Collapsed to an icon until it is used, so a toolbar row fits on a phone. */
export function PageSearch({ value, onChange, placeholder, busy = false, className }: PageSearchProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!open && !value) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('shell.search')}
        className={cn(
          'h-9 w-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center hover:bg-accent transition-colors shrink-0',
          className,
        )}
      >
        <Search className="h-4 w-4" />
      </button>
    );
  }

  return (
    /*
      A floor on the width, because the input's padding alone is 68px — the
      magnifier on one side, the ✕ on the other. Squeezed beside a long title on
      a phone the box came down to exactly that, leaving no room to type, and
      Chrome then puts the caret back at the start after every letter: "בזק"
      went in as "קזב" and found nothing. 9rem leaves room for about eight
      letters; the title gives way instead (TopBar hides it on a phone).
    */
    <div className={cn('relative flex-1 min-w-[9rem] animate-slide-in', className)}>
      {busy ? (
        <span className="absolute end-3 top-1/2 -translate-y-1/2 flex text-muted-foreground pointer-events-none">
          <Spinner />
        </span>
      ) : (
        <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      )}
      {/*
        A text box, deliberately, and not `type="search"`.

        The search type comes with the browser's own clear affordance, and it
        works behind React's back: Escape empties the element while the state
        that drives the request keeps the old term, so the box goes blank and
        the list stays narrowed with nothing on screen explaining why. It also
        draws a second ✕ of its own, next to the one this component already
        has — in RTL both land on the same side, under the magnifier.

        `inputMode` keeps what the type was actually wanted for: a phone
        keyboard whose return key says search. Escape is handled here instead,
        and means what it means everywhere else — close, and clear on the way.
      */}
      <input
        autoFocus
        type="text"
        inputMode="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          onChange('');
          setOpen(false);
        }}
        placeholder={placeholder ?? t('shell.searchPlaceholder')}
        aria-label={t('shell.search')}
        className="w-full h-9 pe-9 ps-8 rounded-lg bg-muted text-sm outline-none placeholder:text-muted-foreground"
      />
      <button
        type="button"
        onClick={() => {
          onChange('');
          setOpen(false);
        }}
        aria-label={t('shell.closeSearch')}
        className="absolute start-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-accent"
      >
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
